import type { ReactNode } from "react";

import { ChevronRight } from "./icons";
import { cn } from "./cn";

export function ListRow({
  title,
  subtitle,
  badge,
  trailing,
  chevron,
  marker = "none",
  kind = "record",
  onClick,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Status chip shown on the right, before the chevron. */
  badge?: ReactNode;
  trailing?: ReactNode;
  /** Show the chevron after `trailing` too; by default only without it. */
  chevron?: boolean;
  marker?: "none" | "system" | "mine" | "success" | "warning";
  kind?: "reminder" | "record" | "setting";
  onClick?: () => void;
}) {
  const content = (
    <>
      {marker !== "none" && (
        <span
          aria-hidden="true"
          data-marker={marker}
          className={cn(
            "size-2 shrink-0 rounded-full",
            marker === "system" && "bg-system",
            marker === "mine" && "bg-accent",
            marker === "success" && "bg-success",
            marker === "warning" && "bg-warning",
          )}
        />
      )}
      <span className="min-w-0 flex-1">
        <strong className="line-clamp-2 block text-body font-semibold tracking-tight text-fg">
          {title}
        </strong>
        {subtitle !== undefined && (
          <span className="line-clamp-2 block text-footnote text-fg-muted">
            {subtitle}
          </span>
        )}
      </span>
      {badge !== undefined && <span className="shrink-0">{badge}</span>}
      {trailing !== undefined && (
        <span className="shrink-0 text-callout font-medium text-fg-muted">
          {trailing}
        </span>
      )}
      {onClick && (chevron ?? trailing === undefined) && (
        <ChevronRight size={16} className="shrink-0 text-fg-subtle" />
      )}
    </>
  );
  const className = cn(
    "relative flex w-full items-center gap-3 bg-transparent px-4 py-3 text-left",
    // Hairline inset 16 from the left; the last row of a list drops it.
    "after:absolute after:bottom-0 after:left-4 after:right-0 after:h-px after:bg-line-subtle after:content-['']",
    kind === "setting" ? "min-h-[52px]" : "min-h-16",
    kind === "reminder" && "min-h-[68px]",
    onClick && "cursor-pointer hover:bg-hover-overlay active:bg-active-overlay",
  );
  return onClick ? (
    <button type="button" data-row="" className={className} onClick={onClick}>
      {content}
    </button>
  ) : (
    <div data-row="" className={className}>
      {content}
    </div>
  );
}
